import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useStoreState, useStoreActions } from 'easy-peasy';

import TheHeader from '@components/TheHeader';
import Footer from '@components/Footer/Footer';
import Masthead from '@components/Masthead';
import Helper from '@components/Helper';
import Stepper from '@components/Stepper';
import Modal from '@components/Modal';
import { ShareLinkModal } from '@components/ShareLinkModal/ShareLinkModal';

import { header, hero } from '@utils/default-values';
import useShareModal from '@utils/hooks/use-share-modal';
import { selectorUser } from '@model/User';
import { MODAL_IDS_MAP } from '@app/constants/modals';
import { GTM } from '@app/gtm';
import { USER_TYPES_MAP } from '@app/constants/session';
import { selectorSession } from '@model/session';
import { LoaderFullPage } from '@app/loaders/LoaderFullPage';
import {
  jobStoreSelector,
  selectorJobIsReverseMarketing,
} from '@model/Job/Job.selectors';
import { candidatesResultSelector } from '@model/Candidate/Candidate.selectors';
import { CompensationVisibilityModal } from '@components/_modals/CompensationVisibilityModal/index';

const MainLayout = ({ children }) => {
  const [helperText, setHelperText] = useState(null);
  const [manager, setManager] = useState(null);

  const location = useLocation();

  // STATE
  const user = useStoreState(selectorUser);
  const job = useStoreState(jobStoreSelector) || {};
  const candidates = useStoreState(candidatesResultSelector) || [];
  const currentRoute = useStoreState(state => state.currentRoute);
  const currentStatus = useStoreState(state => state.currentStatus);
  const isModalOpened = useStoreState(state => state.isModalOpened);
  const isJobReverseMarketing = useStoreState(selectorJobIsReverseMarketing);
  const modalId = useStoreState(state => state.modalId);
  const initialized = useStoreState(state => state.initialized);
  const { loading } = useStoreState(selectorSession);
  const setModalOpened = useStoreActions(state => state.setModalOpened);
  const setModalId = useStoreActions(state => state.setModalId);

  const { handleShareButtonClick } = useShareModal();

  useEffect(() => {
    if (currentRoute) {
      const payload = {
        name: user.firstName,
        context: user.firstName !== null && 'withName',
        isJobReverseMarketing,
        hasCandidates: candidates.length > 0,
      };

      setHelperText(hero(currentStatus, payload).helperText[currentRoute]);
    }
  }, [location, currentRoute, currentStatus, isJobReverseMarketing]);

  useEffect(() => {
    if (job) {
      setManager(job?.recruitmentConsultant ?? job?.salesConsultant);
    }
  }, [job?.recruitmentConsultant, job?.salesConsultant]);

  const handleAnalytics = step => {
    const eventAction = `Navigazione ${
      user.type === USER_TYPES_MAP.MANAGER ? 'prospect' : 'cliente'
    }`;

    GTM.trackGTMEvent({
      action: eventAction,
      label: `${job.companyName} - ${job.id} - ${job.title} - ${step.name}`,
    });

    GTM.trackGA4Event(
      eventAction,
      job.companyName,
      `${job.id} - ${job.title} - ${step.name}`,
    );
  };

  const getSteps = () => {
    return hero().steps({
      isMiroUrlAvailable: !!job.profilingBoardUrl,
      isJobReverseMarketing,
      hasCandidates: candidates.length > 0,
    });
  };

  return (
    <main>
      {!initialized || loading ? (
        <LoaderFullPage />
      ) : (
        <>
          <div className="rev-o-container">
            <div className="rev-o-grid">
              <div className="rev-o-col-12 rev-u-px-16 rev-u-py-16">
                <TheHeader labels={header()} onClick={handleShareButtonClick} />
              </div>
            </div>
          </div>
          <Masthead
            job={job}
            currentRoute={currentRoute}
            isBackToHomepageAvailable
          >
            {{
              left: (
                <div className="c-app-masthead__stepper">
                  <Stepper steps={getSteps()} onRouteChange={handleAnalytics} />
                </div>
              ),
              right: (
                <Helper
                  modifier="c-app-helper--masthead"
                  text={helperText}
                  manager={manager}
                />
              ),
            }}
          </Masthead>
        </>
      )}
      <div className="c-main-layout">{children}</div>
      <Footer
        logo={
          __ENVIRONMENT_CONFIG__.IS_THEME_AKI
            ? `${__ENVIRONMENT_CONFIG__.PUBLIC_URL}/assets/images/logo/aaron-king-logo.png`
            : undefined
        }
        payoff={
          __ENVIRONMENT_CONFIG__.IS_THEME_AKI
            ? `${__ENVIRONMENT_CONFIG__.PUBLIC_URL}/assets/images/logo/aki-payoff.svg`
            : undefined
        }
      />
      <Modal visible={isModalOpened && modalId === MODAL_IDS_MAP.SHARE}>
        <ShareLinkModal />
      </Modal>
      <CompensationVisibilityModal
        isOpen={isModalOpened}
        modalId={modalId}
        onClose={() => {
          setModalOpened(false);
          setModalId(null);
        }}
      />
    </main>
  );
};

export default MainLayout;
